import dayjs from 'dayjs';
import _ from 'lodash';

const normalizeSupplierContact = (contact) => ({
  id: contact?.id ?? contact?.attributes?.id ?? null,
  main_contact: _.isString(contact?.main_contact)
    ? contact.main_contact.trim()
    : _.isString(contact?.attributes?.main_contact)
      ? contact.attributes.main_contact.trim()
      : null,
  main_contact_phone_number: _.isString(contact?.main_contact_phone_number)
    ? contact.main_contact_phone_number.trim()
    : _.isString(contact?.attributes?.main_contact_phone_number)
      ? contact.attributes.main_contact_phone_number.trim()
      : null,
  main_contact_email: _.isString(contact?.main_contact_email)
    ? contact.main_contact_email.trim() || null
    : _.isString(contact?.attributes?.main_contact_email)
      ? contact.attributes.main_contact_email.trim() || null
      : null,
});

const buildSupplierContacts = (attributes = {}) => {
  const contactsRaw = Array.isArray(attributes?.supplier_contacts?.data)
    ? attributes.supplier_contacts.data
    : Array.isArray(attributes?.supplier_contacts)
      ? attributes.supplier_contacts
      : Array.isArray(attributes?.supplier_contact)
        ? attributes.supplier_contact
        : [];

  const normalized = contactsRaw
    .map((contact) => normalizeSupplierContact(contact))
    .filter(
      (contact) =>
        contact?.main_contact ||
        contact?.main_contact_phone_number ||
        contact?.main_contact_email
    );

  if (!normalized.length) {
    const fallback = normalizeSupplierContact(attributes);
    return fallback.main_contact ||
      fallback.main_contact_phone_number ||
      fallback.main_contact_email
      ? [fallback]
      : [];
  }

  return normalized;
};

export const API = {
  suppliers: {
    path: '/suppliers?filters[status][$eq]=true',
    method: 'GET',
    get: null,
    auth: true,
    fallback: null,
    transform: (data) => ({
      meta: data?.meta,
      data: data?.data.map((item) => ({
        id: item?.id,
        attributes: {
          ...item?.attributes,
          category_id: {
            id_category: item?.attributes?.id_category?.data,
            id: item?.id,
          },
          sub_category_id: {
            id_sub_category: item?.attributes?.id_sub_category?.data,
            id: item?.id,
          },
          priority_id: {
            priority: item?.attributes?.priority,
            id: item?.id,
          },
          locations_id: {
            id_locations: item?.attributes?.id_locations?.data,
            id: item?.id,
          },
          notes_id: {
            notes: item?.attributes?.notes,
            id: item?.id,
          },
          supplier_contacts: buildSupplierContacts(item?.attributes),
        },
      })),
    }),
  },
  supplier: {
    path: '/suppliers/{{id}}',
    method: 'GET',
    get: null,
    auth: true,
    fallback: null,
    transform: (data) => {
      const supplierContacts = buildSupplierContacts(data?.data?.attributes);

      return {
        ...data,
        data: {
          ...data?.data,
          attributes: {
            ...data?.data?.attributes,
            supplier_contacts: supplierContacts.length ? supplierContacts : null,
          },
        },
      };
    },
  },
  create: {
    path: '/suppliers',
    method: 'POST',
    get: null,
    auth: true,
    fallback: null,
    adapter: (body) => ({
      data: {
        ...body,
        label: body?.label?.toUpperCase(),
        company_name: body?.company_name?.toUpperCase(),
        status: true,
      },
    }),
  },
  update: {
    path: '/suppliers/{{id}}',
    method: 'PUT',
    get: null,
    auth: true,
    fallback: null,
    adapter: (body) => {
      const supplierContacts = Array.isArray(body?.supplier_contacts)
        ? body.supplier_contacts.map((contact) => ({
          id: contact?.id || null,
          main_contact: _.isString(contact?.main_contact)
            ? contact.main_contact.trim()
            : null,
          main_contact_phone_number: _.isString(contact?.main_contact_phone_number)
            ? contact.main_contact_phone_number.trim()
            : null,
          main_contact_email: _.isString(contact?.main_contact_email)
            ? contact.main_contact_email.trim() || null
            : null,
        }))
        : [];

      return {
        data: {
          ...body,
          priority: body?.priority?.id ? body?.priority?.id : null,
          services_on_board:
            body.services_on_board === true
              ? 'Yes'
              : body.services_on_board === false
                ? 'No'
                : null,
          supplier_contacts: supplierContacts,
          target: body?.target?.id ? body?.target?.id : null,
          stars: body?.stars?.id ? body?.stars?.id : null,
          id_departments: body.id_departments.map((dep) => dep.id),
          id_languages: body.id_languages.map((lang) => lang.id),
          id_locations: body.id_locations.map((loc) => loc.id),
          opening_date: body?.opening_date
            ? dayjs(body?.opening_date).format('YYYY-MM-DD')
            : null,
          closing_date: body?.closing_date
            ? dayjs(body?.closing_date).format('YYYY-MM-DD')
            : null,
          id_category: body?.id_category?.map((category) => category.id),
          id_sub_category: body?.id_sub_category?.map((subCategory) => subCategory.id),
        },
      };
    },
  },
  otherLanguages: {
    path: '/languages?pagination[page]=1&pagination[pageSize]=1000&fields[0]=nation&fields[1]=language&sort[0]=id:asc',
    method: 'GET',
    get: null,
    auth: true,
    fallback: null,
    transform: (data) => {
      return data?.data.map((item) => ({
        id: item.id,
        name: `${item.attributes.nation} - ${item.attributes.language}`,
      }));
    },
  },
  delete: {
    path: '/suppliers/{{id}}',
    method: 'DELETE',
    get: null,
    auth: true,
    fallback: null,
  },
  suppliersSync: {
    path: '/mexal-gateway/import-from-mexal',
    method: 'POST',
    get: null,
    auth: true,
    fallback: null,
    adapter: () => ({
      data: {
        import_api: 'fornitori',
        commit: true,
      },
    }),
  },
  itemsSync: {
    path: '/mexal-gateway/import-from-mexal',
    method: 'POST',
    get: null,
    auth: true,
    fallback: null,
    adapter: () => ({
      data: {
        import_api: 'articoli',
        commit: true,
      },
    }),
  },

  select: {
    path: '/suppliers?pagination[page]=1&pagination[pageSize]=10000&sort[company_name]=asc&filters[status][$eq]=true',
    method: 'GET',
    get: null,
    auth: true,
    fallback: [],
    transform: (data) =>
      data?.data.map((item) => ({
        name: `${item?.attributes?.mexal_code} - ${item.attributes.label} - ${item.attributes.company_name}`,
        id: item.id,
      })),
  },

  updateFromList: {
    path: '/suppliers/{{id}}',
    method: 'PUT',
    get: null,
    auth: true,
    fallback: null,
    adapter: (body) => {
      const id_category = _.has(body, 'id_category')
        ? { id_category: body?.id_category?.map((category) => category.id) || null }
        : null;
      const priority = _.has(body, 'priority')
        ? { priority: body?.priority?.id || null }
        : null;
      const id_sub_category = _.has(body, 'id_sub_category')
        ? {
          id_sub_category:
            body?.id_sub_category?.map((subCategory) => subCategory.id) || null,
        }
        : null;
      const id_locations = _.has(body, 'id_locations')
        ? { id_locations: body?.id_locations.map((loc) => loc.id) || null }
        : null;
      return {
        data: {
          ..._.omit(body, ['id_category', 'priority', 'id_sub_category']),
          ...id_category,
          ...priority,
          ...id_sub_category,
          ...id_locations,
        },
      };
    },
  },
};
